import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';

const MedicationModal = ({ open, onClose, onSubmit }) => {
  const emptyForm = {
    drugName: '',
    dose: '',
    route: '',
    frequency: '',
    duration: '',
    indication: '',
    prnInstructions: '',
    startDate: '',
    endDate: '',
  };

  const [formData, setFormData] = useState({
    ...emptyForm,
  });

  const [medications, setMedications] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const formRef = useRef(null);

  const handleAddToList = (e) => {
    e.preventDefault();
    if (!formData.drugName || !formData.dose || !formData.route || !formData.frequency) return;
    
    if (editingId) {
      // Update existing item
      setMedications((prev) =>
        prev.map((m) =>
          m.id === editingId ? { ...m, ...formData } : m
        )
      );
      setEditingId(null);
    } else {
      // Add new item
      setMedications((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          ...formData,
          status: 'active',
          stoppedAt: null,
          addToTaskList: false,
        },
      ]);
    }
    setFormData({ ...emptyForm });
  };

  const handleToggleItemTaskList = (id) => {
    setMedications((prev) =>
      prev.map((m) => (m.id === id ? { ...m, addToTaskList: !m.addToTaskList } : m))
    );
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleRemove = (id) => {
    setMedications((prev) => prev.filter((m) => m.id !== id));
    if (editingId === id) {
      setEditingId(null);
      setFormData({ ...emptyForm });
    }
  };

  const handleEdit = (item) => {
    setFormData({
      drugName: item.drugName,
      dose: item.dose,
      route: item.route,
      frequency: item.frequency,
      duration: item.duration || '',
      indication: item.indication || '',
      prnInstructions: item.prnInstructions || '',
      startDate: item.startDate || '',
      endDate: item.endDate || ''
    });
    setEditingId(item.id);
  };

  useEffect(() => {
    if (editingId && formRef.current) {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [editingId]);

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({ ...emptyForm });
  };

  const handleSaveCarePlan = () => {
    if (!medications.length) return;
    const hasTaggedItems = medications.some((m) => m.addToTaskList);
    onSubmit({ medications }, { addToTaskList: hasTaggedItems });
    setMedications([]);
    setFormData({ ...emptyForm });
    onClose();
  };

  const handleClose = () => {
    setMedications([]);
    setFormData({ ...emptyForm });
    setEditingId(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Medication Care Plan</DialogTitle>
      <form onSubmit={handleAddToList}>
        <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Box ref={formRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Drug Name *</Typography>
                <TextField
                  fullWidth
                  size="small"
                  required
                  placeholder="e.g., Amoxicillin"
                  value={formData.drugName}
                  onChange={handleChange('drugName')}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Dose *</Typography>
                <TextField
                  fullWidth
                  size="small"
                  required
                  placeholder="e.g., 500mg"
                  value={formData.dose}
                  onChange={handleChange('dose')}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Route *</Typography>
                <TextField
                  fullWidth
                  size="small"
                  required
                  placeholder="e.g., Oral, IV, IM"
                  value={formData.route}
                  onChange={handleChange('route')}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Frequency *</Typography>
                <TextField
                  fullWidth
                  size="small"
                  required
                  placeholder="e.g., TID, BID, QD"
                  value={formData.frequency}
                  onChange={handleChange('frequency')}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Duration <Typography component="span" color="text.secondary">(Optional)</Typography>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g., 7 days"
                  value={formData.duration}
                  onChange={handleChange('duration')}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Indication <Typography component="span" color="text.secondary">(Optional)</Typography>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g., Bacterial infection"
                  value={formData.indication}
                  onChange={handleChange('indication')}
                />
              </Grid>
            </Grid>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                PRN Instructions <Typography component="span" color="text.secondary">(Optional)</Typography>
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Instructions for as-needed medication"
                value={formData.prnInstructions}
                onChange={handleChange('prnInstructions')}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Start Date <Typography component="span" color="text.secondary">(Optional)</Typography>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={formData.startDate}
                  onChange={handleChange('startDate')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  End Date <Typography component="span" color="text.secondary">(Optional)</Typography>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={formData.endDate}
                  onChange={handleChange('endDate')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {medications.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Medications in this care plan ({medications.length}) – tag to add to task list
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {medications.map((m) => (
                    <Paper
                      key={m.id}
                      elevation={0}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: m.addToTaskList ? 'primary.main' : 'divider',
                        backgroundColor: m.addToTaskList ? 'primary.50' : 'grey.50',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1, minWidth: 0 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={m.addToTaskList}
                              onChange={() => handleToggleItemTaskList(m.id)}
                              sx={{ mt: -0.5 }}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                              Add to task list
                            </Typography>
                          }
                          sx={{ mt: -0.5, mr: 1 }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {m.drugName} {m.dose}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {`${m.route}${m.frequency ? ` • ${m.frequency}` : ''}`}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <Tooltip title="Edit medication">
                          <IconButton
                            size="small"
                            color={editingId === m.id ? 'primary' : 'default'}
                            onClick={() => handleEdit(m)}
                            aria-label="Edit medication"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          aria-label="Remove medication"
                          onClick={() => handleRemove(m.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="outlined">Cancel</Button>
          {editingId && (
            <Button onClick={handleCancelEdit} variant="outlined">Cancel Edit</Button>
          )}
          <Button type="submit" variant="outlined">{editingId ? 'Update Item' : 'Add to List'}</Button>
          <Button
            onClick={handleSaveCarePlan}
            variant="contained"
            disabled={!medications.length}
          >
            Save Care Plan
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MedicationModal;
